/* eslint-disable sonarjs/no-duplicate-string */
import * as React from 'react'
import styled from 'styled-components'
import { style } from 'typestyle'
import { IntroTextSpan, EllipsisDiv } from '../../../common/src/components/core'
import { Fonts, ColorName, Colors } from '../../../common/src/constants'
import { diffMins, startOfDay } from '../../../common/src/date'
import { CalendarElementProps, ElemType } from './agentCalendar'

/**
 * Each block is 48px of white background + 1px of light-gray border
 */
export const BLOCK_HEIGHT = 49
/**
 * Since each block is one hour, UNIT_HEIGHT is the y-size of a single minute
 */
export const UNIT_HEIGHT = BLOCK_HEIGHT / 60

export const wrapCalElementClass = style({
  flexGrow: 1,
  paddingLeft: '8px',
})

/**
 * Separator is gray right up until the parent's right edge. The last gap is pure white.
 */
export const HourElementSeparatorRoot = styled.div({
  width: '100%',
  height: '1px',
  position: 'relative',
  left: '-8px',
  zIndex: 2,
  border: 'none',
  borderRight: '8px solid white',
  backgroundColor: '#d8d8d8',
})

export const calendarElementClass = (props: CalendarElementProps) =>
  style({
    position: 'absolute',
    backgroundColor: props.backgroundColor,
    backgroundImage: props.backgroundImage || 'none',
    height: props.elemHeight,
    top: props.top,
    left: '18px',
    opacity: props.opacity,
    zIndex: props.zIndex,
    width: props.elemWidth,
    marginLeft: '42px',
    borderRight: lightGrayBorder,
    borderLeft: lightGrayBorder,
    borderRadius: `${getRoundedCornersForHour(props.hour || 0)}`,
    borderTop: `${props.top === 0 ? lightGrayBorder : 'none'}`,
    transition: 'all 0.5s ease-in-out',
    cursor: props.cursor,
  })

const lightGrayBorder = '1px solid #d8d8d8'

/**
 * The white-with-gray-border background of a calendar grid row. Sits behind everything else, including:
 *
 *   (black rounded rects)  showings
 *     (translucent black)  "off" hours, not working
 *            (pure white)  working hours, doing showings
 *         (striped coral)  calendar block: busy
 *          (striped mint)  calendar block: free
 */
export const hourElementClass = (hour: number) =>
  style({
    backgroundColor: 'white',
    width: '100%',
    height: '48px',
    marginLeft: 'auto',
    borderRight: lightGrayBorder,
    borderLeft: lightGrayBorder,
    borderRadius: `${getRoundedCornersForHour(hour)}`,
    borderTop: `${hour === 1 ? lightGrayBorder : 'none'}`,
    borderBottom: `${hour === 24 ? lightGrayBorder : 'none'}`,
  })

/**
 * Container for all showings elements. They're laid out in this container using
 * percentage widths, like '33%'. This means the ShowingElements that overlap in
 * time can still display nicely on any size screen.
 */
export const ShowingElementsRoot = styled.div({
  marginLeft: '70px',
  width: '456px',
  position: 'absolute',
  maxWidth: '100%',
  // mobile version is full-width on its own line w margin to separate from DateHeader
  $nest: {
    '@media only screen and (max-width: 600px)': {
      maxWidth: 'calc(100% - 64px)',
    },
  },
})

/**
 * Given an hour of the day (e.g. 1pm), infer how we should treat its border-radius
 */
function getRoundedCornersForHour(hour: number) {
  switch (hour) {
    case 1:
      // round top corners
      return '4px 4px 0px 0px' as string
    case 24:
      // round bottom corners
      return '0px 0px 4px 4px' as string
    default:
      return '0px' as string
  }
}

/**
 * Flex container that holds all three headers like:
 *
 * [date header]    [name / email header]    [sync button]
 */
export const AgentCalendarHeaderRoot = styled.div({
  display: 'flex',
  flexWrap: 'wrap-reverse',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginTop: '96px',
  marginLeft: '58px',
  paddingBottom: '48px',
  $nest: {
    '@media only screen and (max-width: 1100px)': {
      maxWidth: '480px',
      width: 'calc(100% - 58px)',
      paddingRight: '8px',
      boxSizing: 'border-box',
      marginTop: '32px',
    },
  },
})

const hourElementTextOuterClass = style({
  height: '49px',
})

const hourElementTextInnerClass = style({
  fontFamily: Fonts.mono,
  whiteSpace: 'pre-wrap',
  marginTop: '40px',
  minWidth: '44px',
  color: 'silver',
  fontWeight: 'lighter',
  fontSize: '14px',
  width: 'fit-content',
  textAlign: 'right',
  marginRight: '8px',
})

/**
 * Light gray monospaced text showing the hour of a calendar grid row. (E.g. "1pm")
 */
export const HourElementText = (props: { children?: React.ReactNode }) => (
  <div className={hourElementTextOuterClass}>
    <div className={hourElementTextInnerClass}>{props.children}</div>
  </div>
)

interface CalendarDownArrowProps {
  isDown: boolean
  color: ColorName
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

const CalendarDownArrowWrapper = styled.div({
  position: 'absolute',
  zIndex: 2,
})

const CalendarDownArrowInner = styled.div<{ isDown: boolean }>(props => ({
  userSelect: 'none',
  display: 'inline-block',
  transform: `rotate(${props.isDown ? '-90' : '90'}deg)`,
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '24px',
}))

/**
 * A TypeStyle implementation of our core component: DownArrow
 */
export const CalendarDownArrow = (props: CalendarDownArrowProps) => (
  <CalendarDownArrowWrapper>
    <IntroTextSpan color={props.color}>
      <CalendarDownArrowInner isDown={props.isDown} onClick={props.onClick}>
        ›
      </CalendarDownArrowInner>
    </IntroTextSpan>
  </CalendarDownArrowWrapper>
)

const DatePickerInput = styled.input({
  display: 'inline-block',
  width: '24px',
  cursor: 'pointer',
  fontWeight: 'bold',
  border: 'none',
  backgroundColor: '#f6f6f7',
  marginTop: '10px',
})

const DatePickerWrapper = styled.span({
  position: 'absolute',
  zIndex: 5,
  opacity: 0,
})

/**
 * The input field used for picking dates.
 */
export const DatePicker = (props: { id?: string }) => (
  <DatePickerWrapper>
    <DatePickerInput id={props.id}></DatePickerInput>
  </DatePickerWrapper>
)

/**
 * Two-column container holds all of:
 *
 * [date header]          [name / email header]       [sync button]
 *
 * [          calendar grid row               ]       [note]
 * [          calendar grid row               ]
 * [          calendar grid row               ]       [note]
 */
export const AgentCalendarContainer = styled.blockquote({
  margin: '0 auto',
  width: '80%',
  maxWidth: '900px',
  wordBreak: 'break-word',

  // mobile container has less margin
  $nest: {
    '@media only screen and (max-width: 680px)': {
      width: '100%',
      maxWidth: '533px',
    },
  },
})

export const InputDateLargeRoot = styled.div({
  marginLeft: '58px',
  marginBottom: '54px',
  position: 'relative',
  zIndex: 2,
  width: '192px',
  height: '26px',
  marginTop: '-80px',
})

/**
 * Big date at the top of the page "« Thu Mar 12 »" ... with "prev day" and "next day" buttons
 */
export const DateHeader = styled.div({
  color: '#29292b',
  fontSize: '32px',
  display: 'flex',
  flexWrap: 'wrap',
  fontFamily: Fonts.sansBody,
  backgroundColor: Colors.snow,
  position: 'relative',
  marginLeft: '-30px', // move left to hang "prev day" button in the margin
  zIndex: 5,
  width: '260px',
  paddingTop: '6px', // center-aligns with NameEmailHeader

  // mobile version is full-width on its own line w margin to separate from DateHeader
  $nest: {
    '@media only screen and (max-width: 680px)': {
      left: 0,
      width: '100%',
      maxWidth: '100%',
      minWidth: '260px',
    },
  },
})

const DateHeaderWeekdayElement = styled.div({
  color: Colors.charcoal,
  display: 'inline',
  fontWeight: 'bold',
  fontFamily: Fonts.sansHeader,
  fontSize: '32px',
  minHeight: 'fit-content',
})

/**
 * Just the day of week, e.g. "Thu"
 */
export const DateHeaderWeekday = (props: { dayOfWeek: string }) => (
  <DateHeaderWeekdayElement>{props.dayOfWeek}</DateHeaderWeekdayElement>
)

const DateHeaderMonthDayRoot = styled.div({
  color: Colors.charcoal,
  fontFamily: Fonts.sansHeader,
  display: 'inline',
  fontSize: '32px',
  minHeight: 'fit-content',
})

/**
 * Title element with month and numeric day, e.g. "Mar 11"
 */
export const DateHeaderMonthDay = (props: { monthDay: string }) => (
  <DateHeaderMonthDayRoot>{props.monthDay}</DateHeaderMonthDayRoot>
)

const NameEmailHeaderWrapper = styled.div({
  textAlign: 'right',
  display: 'inline-block',
  fontFamily: Fonts.sansBody,
  width: '240px',
  color: Colors.midway,

  // mobile version is full-width on its own line w margin to separate from DateHeader
  $nest: {
    '@media only screen and (max-width: 680px)': {
      marginBottom: '32px',
      marginTop: '16px',
      width: '100%',
    },
  },
})

const NameEmailHeaderEmail = styled.span({
  fontFamily: Fonts.mono,
  fontSize: '14px',
  fontWeight: 'lighter',
  color: Colors.midway,
})

/**
 * At the top of the page, to the right of the date:
 *
 *               [agent name truncated if too lon...]
 *                    [email truncated if too lon...]
 */
export const NameEmailHeader = (props: { name: string; emailAddress?: string | null }) => (
  <NameEmailHeaderWrapper>
    <EllipsisDiv>
      <IntroTextSpan color="charcoal">{props.name}</IntroTextSpan>
    </EllipsisDiv>
    <EllipsisDiv>
      <NameEmailHeaderEmail>{props.emailAddress}</NameEmailHeaderEmail>
    </EllipsisDiv>
  </NameEmailHeaderWrapper>
)

const CurrentTimeBarRoot = styled.div<{ top: number }>(props => ({
  position: 'absolute',
  backgroundColor: '#EA4336',
  width: '93%',
  height: '1px',
  border: '1.5px solid #EA4336',
  borderRadius: '4px', // round edges as much as possible
  top: props.top,
  left: '8%',
  zIndex: 5, // above: showings, blocks, work hours; beneath: modals
  $nest: {
    '&::after': {
      content: "''",
      position: 'absolute',
      left: '-8px',
      top: '-7.5px',
      width: '16px',
      height: '16px',
      border: 'none',
      backgroundColor: '#EA4336',
      borderRadius: '8px',
    },
  },
}))

// Bright red horizontal line slightly wider than the calendar grid:  always shows the current time.
export function CurrentTimeBar(props: { date: string; isToday: boolean }) {
  if (!props.isToday) {
    return <></>
  }
  const mins = diffMins(new Date(props.date), startOfDay(new Date(props.date)))
  const top = UNIT_HEIGHT * mins
  return <CurrentTimeBarRoot top={top} />
}

/**
 * Title of Lisa showing on the calendar grid before its clicked, e.g. —
 *
 * "JOHN · Stone Lodge · BR1 showing"
 */
export const ShowingText = styled.p({
  fontFamily: Fonts.sansBody,
  color: 'inherit',
  fontWeight: 500, // any bolder and it's hard to read
  fontSize: '14px',
  letterSpacing: 'normal',
  textShadow: 'inherit',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  margin: '0',
})

/**
 * Virtual/in-person icon to the left of the showing text
 */
export const TourTypeIconRoot = styled.div({
  position: 'relative',
  top: '1px',
})

const StrikethroughSpanRoot = styled.span<{ strikethrough: boolean }>(props => ({
  height: '100%',
  width: '100%',
  display: 'inline-block',
  textDecoration: props.strikethrough ? 'line-through' : 'none',
  whiteSpace: 'nowrap', // change to 'normal' to let each ShowingText description wrap to next line
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  margin: 0,
}))

/**
 * Takes a parameter "strikethrough" which can show child elements "struck out" with a line through any text element
 */
export const StrikethroughSpan = (props: { strikethrough: boolean; children?: React.ReactNode }) => (
  <StrikethroughSpanRoot strikethrough={props.strikethrough}>{props.children}</StrikethroughSpanRoot>
)

/**
 * Virtual/in-person icon to the left of the showing text
 */
export const SyncHeader = styled.div({
  width: '320px',
  minWidth: 'fit-content',
  textAlign: 'right',

  $nest: {
    '@media only screen and (max-width: 1100px)': {
      width: '100%',
      maxWidth: '100%',
      marginBottom: '24px',
    },
  },
})

/**
 * The sync button itself: black text on a sky blue background.
 */
export const syncButtonClass = style({
  border: `1px solid ${Colors.sky}`,
  borderRadius: '4px',
  backgroundColor: `${Colors.sky}`,
  font: `16px ${Fonts.mono}`,
  float: 'right',
  width: '74px',
  textAlign: 'center',
  padding: '8px 0px 8px 0px',
  cursor: 'pointer',
  textShadow: '0 1px rgba(255, 255, 255, 0.25)',
  transition: 'box-shadow .75s ease-out',
  $nest: {
    '&:hover': {
      boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.15)',
      textShadow: '0 1px rgba(255, 255, 255, 0.50)',
    },
  },
})

/**
 * Virtual/in-person icon to the left of the showing text
 */
export const AgentCalendarBodyRoot = styled.div({
  display: 'inline',
})

/**
 * Virtual/in-person icon to the left of the showing text
 */
export const AgentCalendarGrid = styled.div({
  position: 'relative',
  display: 'block',
  maxWidth: '533px',
  flexGrow: 4,
})

export const ElementsAndLabels = styled.div({
  display: 'flex',
})

/**
 * Virtual/in-person icon to the left of the showing text
 */
export const AgentCalendarLabels = styled.div({
  display: 'block',
})

const ShowingElementBase = styled.div<CalendarElementProps>(props => ({
  boxSizing: 'border-box',
  position: 'absolute',
  backgroundColor: props.strikethrough ? Colors.snow : Colors.charcoal,
  color: props.strikethrough ? Colors.steel : Colors.white,
  borderRadius: '4px',
  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.25)',
  display: 'flex',
  alignItems: 'stretch',
  height: props.elemHeight,
  width: props.elemWidth,
  top: props.top,
  left: props.left,
  zIndex: props.zIndex,
  minHeight: '21px',
  padding: '2px 12px 0px 12px',
  textDecoration: props.strikethrough ? 'line-through' : 'none',
  border: props.strikethrough ? '1px solid ' + Colors.silver : 'none',
  textShadow: props.strikethrough ? 'none' : '0px 2px rgba(0,0,0,0.50)',
  opacity: props.opacity,
  cursor: 'pointer',
  $nest: {
    '&:hover': {
      opacity: 1,
    },
  },
}))

/**
 * A black rounded rect on the calendar grid that contains Lisa showing info.
 */
export const ShowingElement = (props: CalendarElementProps) => {
  const { strikethrough, elemHeight, elemWidth, top, left, zIndex, opacity } = props
  return (
    <ShowingElementBase
      onClick={props.onClick}
      strikethrough={strikethrough}
      elemHeight={elemHeight}
      elemWidth={elemWidth}
      top={top}
      left={left}
      zIndex={zIndex}
      opacity={opacity}
    >
      {props.children}
    </ShowingElementBase>
  )
}

const CalendarLabelWrapper = styled.div<{ top: number }>(props => ({
  display: 'flex',
  position: 'absolute',
  top: props.top,
  fontFamily: Fonts.sansBody,
}))

const CalendarLabelLine = styled.div({
  width: '64px',
  height: '1px',
  zIndex: 5,
  border: 'none',
  backgroundColor: '#d8d8d8',
  margin: '0px 12px 0px 2px',
})

const CalendarLabelText = styled.p<{ opacity: number }>(props => ({
  fontSize: '14px',
  marginTop: '-7px',
  color: '#59595d',
  opacity: props.opacity,
}))

const CalendarLabelStatus = styled.span<{ status?: ElemType; opacity: number }>(props => ({
  border: `solid 1px ${props.status === ElemType.BLOCKED ? Colors.coral : Colors.mint}`,
  height: '16px',
  marginTop: '-11px',
  borderRadius: '2px',
  fontSize: '14px',
  color: props.status === ElemType.BLOCKED ? Colors.coral : Colors.mint,
  textAlign: 'center',
  padding: '2px 5px 2px 5px',
  marginRight: '8px',
  overflow: 'hidden',
  opacity: props.opacity,
}))

const CalendarLabelCalSourceText = styled.p<{ opacity: number }>(props => ({
  fontSize: '16px',
  marginTop: '-8px',
  color: '#29292b',
  opacity: props.opacity,
}))

export const CalendarLabel = (props: { top: number; text: string; opacity: number; status?: ElemType }) => (
  <CalendarLabelWrapper top={props.top}>
    <CalendarLabelLine />
    {props.status === ElemType.BLOCKED && (
      <CalendarLabelStatus status={props.status} opacity={props.opacity}>
        BUSY
      </CalendarLabelStatus>
    )}
    {props.status === ElemType.FREE && (
      <CalendarLabelStatus status={props.status} opacity={props.opacity}>
        FREE
      </CalendarLabelStatus>
    )}
    {props.status === undefined ? (
      <CalendarLabelText opacity={props.opacity}>{props.text}</CalendarLabelText>
    ) : (
      <CalendarLabelCalSourceText opacity={props.opacity}>{props.text}</CalendarLabelCalSourceText>
    )}
  </CalendarLabelWrapper>
)

export const labelsWrapperClass = style({
  flexGrow: 2,
  position: 'relative',
})

export const Link = styled.a({
  color: Colors.sky,
  fontWeight: 'normal',
  textDecoration: 'none',
})
